// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

// Utility for authenticated API calls to remote Stargate devices
import axios from 'axios';
import { retryAsync } from './retry';

/**
 * Default retry configuration for remote API calls
 * Optimized for UI responsiveness
 */
const RETRY_CONFIG = {
  maxRetries: 2, // Reduced retries for faster feedback (initial + 1 retry)
  initialDelay: 500, // Shorter initial delay
  maxDelay: 2000, // Shorter maximum delay between retries
  timeout: 8000, // Slightly longer timeout but fewer retries
};

/**
 * Make an authenticated GET request to the Stargate device via backend proxy.
 * @param {string} ip - Device IP address
 * @param {string} apiKey - API key for authentication
 * @param {string} path - API endpoint path (e.g., '/api/system/info')
 * @returns {Promise<any>} - Response data
 */
export async function remoteGet(ip, apiKey, path) {
  return await makeRequestWithProtocolFallback(path, 'GET', ip, apiKey);
}

/**
 * Make an authenticated POST request to the Stargate device via backend proxy.
 * @param {string} ip - Device IP address
 * @param {string} apiKey - API key for authentication
 * @param {string} path - API endpoint path (e.g., '/api/system/info')
 * @param {object} data - Request data
 * @returns {Promise<any>} - Response data
 */
export async function remotePost(ip, apiKey, path, data) {
  return await makeRequestWithProtocolFallback(path, 'POST', ip, apiKey, data);
}

/**
 * Make an authenticated PUT request to the Stargate device via backend proxy.
 * @param {string} ip - Device IP address
 * @param {string} apiKey - API key for authentication
 * @param {string} path - API endpoint path
 * @param {object} data - Request data
 * @returns {Promise<any>} - Response data
 */
export async function remotePut(ip, apiKey, path, data) {
  return await makeRequestWithProtocolFallback(path, 'PUT', ip, apiKey, data);
}

/**
 * Make a request with HTTPS/HTTP protocol fallback
 * @param {string} path - API endpoint path
 * @param {string} method - HTTP method (GET/POST)
 * @param {string} ip - Device IP address
 * @param {string} apiKey - API key for authentication
 * @param {object} data - Request data (for POST requests)
 * @returns {Promise<any>} - Response data
 */
async function makeRequestWithProtocolFallback(path, method, ip, apiKey, data = null) {
  const token = localStorage.getItem('token');

  // Try HTTPS first, then HTTP if HTTPS fails
  const protocols = ['https', 'http'];
  let lastError = null;

  for (const protocol of protocols) {
    try {
      const url = method === 'GET' ? buildProxyGetUrl(path) : buildProxyPostUrl(path);

      const request = async () => {
        const config = {
          params: {
            ip,
            api_key: apiKey,
            force_protocol: protocol, // Tell backend proxy which protocol to use
          },
          timeout: RETRY_CONFIG.timeout,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-API-Key': apiKey,
          },
        };

        let response;
        if (method === 'GET') {
          response = await axios.get(url, config);
        } else if (method === 'PUT') {
          response = await axios.put(url, data, config);
        } else {
          // POST
          if (data && Object.keys(data).length > 0) {
            response = await axios.post(url, data, config);
          } else {
            response = await axios.post(url, undefined, config);
          }
        }

        return response.data;
      };

      // Try the request with retries for this protocol
      const result = await retryAsync(request, RETRY_CONFIG.maxRetries, RETRY_CONFIG.initialDelay);

      // If we get here, the request succeeded
      return result;
    } catch (err) {
      lastError = err;

      // If this is not a connection/protocol related error, don't try the other protocol
      if (err.response) {
        const status = err.response.status;
        // Only try HTTP fallback for certain error types that might be protocol-related
        if (
          status !== 502 &&
          status !== 503 &&
          status !== 504 &&
          status !== 521 &&
          status !== 522 &&
          status !== 523
        ) {
          // This is not a protocol-related error, throw immediately
          throw err;
        }
      }

      // Log the attempt but continue to next protocol (only log this, not the full axios error)
      console.warn(
        `${method} request to ${path} failed with ${protocol.toUpperCase()}, trying next protocol...`
      );
    }
  }

  // If we get here, both protocols failed
  console.error(`API request to ${path} failed with both HTTPS and HTTP protocols:`, lastError);
  throw lastError;
}

// Helper to construct the proxy endpoint URL for GET requests
function buildProxyGetUrl(path) {
  // Remove leading '/api/proxy' or '/api' if present, then prepend '/api/proxy'
  let cleanPath = path;
  if (cleanPath.startsWith('/api/proxy/')) {
    cleanPath = cleanPath.slice(10); // remove '/api/proxy'
  } else if (cleanPath.startsWith('/api/')) {
    cleanPath = cleanPath.slice(4); // remove '/api'
  }
  return `/api/proxy${cleanPath}`;
}

// Helper to construct the proxy endpoint URL for POST requests
function buildProxyPostUrl(path) {
  // Remove leading '/api/proxy' or '/api' if present, then prepend '/api/proxy'
  let cleanPath = path;
  if (cleanPath.startsWith('/api/proxy/')) {
    cleanPath = cleanPath.slice(10); // remove '/api/proxy'
  } else if (cleanPath.startsWith('/api/')) {
    cleanPath = cleanPath.slice(4); // remove '/api'
  }
  return `/api/proxy${cleanPath}`;
}
